/**
 * Week-slot math for the strike price schedule.
 * The week is a ring of 168 hourly slots; shifting by a whole-hour offset wraps across the week boundary.
 * Days are 0-based with Sunday as 0 in both the UTC sparse shape and the local matrix.
 */

export const DAYS_PER_WEEK = 7;
export const HOURS_PER_DAY = 24;
const SLOTS_PER_WEEK = DAYS_PER_WEEK * HOURS_PER_DAY;

export function newEmptyMatrix() {
  return Array.from({ length: DAYS_PER_WEEK }, () => Array(HOURS_PER_DAY).fill(undefined));
}

export function isMatrixEmpty(matrix) {
  return matrix.every(row => row.every(v => !hasPrice(v)));
}

export function utcSparseToLocalMatrix(sparse, offsetHours) {
  const matrix = newEmptyMatrix();
  if (!Array.isArray(sparse)) {
    return matrix;
  }
  sparse.forEach(entry => {
    if (!isSlot(entry.dayOfWeek, entry.hourOfDay)) {
      return;
    }
    const local = shiftSlot(entry.dayOfWeek, entry.hourOfDay, offsetHours);
    matrix[local.day][local.hour] = entry.strikePrice;
  });
  return matrix;
}

export function localMatrixToUtcSparse(matrix, offsetHours) {
  const result = [];
  for (let day = 0; day < DAYS_PER_WEEK; day++) {
    for (let hour = 0; hour < HOURS_PER_DAY; hour++) {
      const value = matrix[day][hour];
      if (hasPrice(value)) {
        const utc = shiftSlot(day, hour, -offsetHours);
        result.push({ dayOfWeek: utc.day, hourOfDay: utc.hour, strikePrice: Number(value) });
      }
    }
  }
  return result.sort((a, b) => (a.dayOfWeek - b.dayOfWeek) || (a.hourOfDay - b.hourOfDay));
}

export function hasPrice(value) {
  return value !== undefined && value !== null && value !== '';
}

function isSlot(day, hour) {
  return Number.isInteger(day) && day >= 0 && day < DAYS_PER_WEEK
    && Number.isInteger(hour) && hour >= 0 && hour < HOURS_PER_DAY;
}

function shiftSlot(day, hour, offsetHours) {
  const raw = (day * HOURS_PER_DAY + hour + offsetHours) % SLOTS_PER_WEEK;
  const index = raw < 0 ? raw + SLOTS_PER_WEEK : raw;
  return { day: Math.floor(index / HOURS_PER_DAY), hour: index % HOURS_PER_DAY };
}
